// ODS-EWO-033 P3. Every command the shell can offer, derived from what the
// applications declare rather than from a list maintained beside them. Wholly
// deterministic: a term is matched, never interpreted, and nothing here executes
// a governed action — a command carries a person to the surface that owns the
// decision, and that surface still asks for it. So the palette can add no
// authority and can refuse nothing on its own.

import { GLOBAL_NAV, ORGANIZATION_NAV, type NavEntry } from "./applications";
import type { Shell, ShellApplication } from "./useShell";

export type CommandKind = "navigate" | "offer";

export type Command = {
  /** Stable across renders, and unique within the palette. */
  readonly id: string;
  readonly label: string;
  /** The heading a person reads it under. */
  readonly group: string;
  readonly icon: string;
  readonly to: string;
  readonly kind: CommandKind;
  /**
   * Terms that should find the command but are not in its label, so matching
   * stays a declared property rather than a heuristic over route paths.
   */
  readonly keywords: readonly string[];
};

/** Longer than any label a person types, and short enough to stay a filter. */
export const MAXIMUM_TERM = 80;

const STUDIO = "Studio";
const ORGANIZATION = "Organization";

function navCommand(
  group: string,
  entry: NavEntry,
  prefix: string,
  keywords: readonly string[],
): Command {
  return {
    id: `nav:${entry.to}`,
    label: prefix ? `${prefix}: ${entry.label}` : entry.label,
    group,
    icon: entry.icon,
    to: entry.to,
    kind: "navigate",
    keywords,
  };
}

function applicationCommands(app: ShellApplication): Command[] {
  const { manifest } = app;
  const keywords = [manifest.name, manifest.id];
  const open: Command = {
    id: `open:${manifest.id}`,
    label: `Open ${app.name}`,
    group: app.name,
    icon: app.icon,
    to: manifest.primaryRoute,
    kind: "navigate",
    keywords,
  };
  const inside = manifest.nav
    // The application's landing route is already "Open …"; two commands for one
    // destination reads as a duplicate rather than a choice.
    .filter((entry) => entry.to !== manifest.primaryRoute)
    .map((entry) => navCommand(app.name, entry, app.name, keywords));
  return [open, ...inside];
}

function offerCommand(app: ShellApplication): Command | null {
  if (app.offer !== "trial" && app.offer !== "subscribe") return null;
  return {
    id: `offer:${app.manifest.id}`,
    label: app.offer === "trial" ? `Start a trial of ${app.name}` : `Get ${app.name}`,
    group: "Not in this organization yet",
    icon: app.icon,
    to: `/marketplace/apps/${app.manifest.catalogSlug}`,
    kind: "offer",
    keywords: [app.manifest.name, app.manifest.id],
  };
}

/**
 * True when a command's destination is one this organization can actually
 * enter. A command that lands on a refusal is worse than no command, so an
 * application the customer does not hold is offered rather than navigated —
 * except when the platform did not answer at all, where the shell keeps
 * navigating and each route still checks for itself (ODS-UX-001 §11).
 */
function enterable(app: ShellApplication, entitlementsKnown: boolean): boolean {
  return app.canEnter || !entitlementsKnown;
}

/** Every command available now, in the order a person should read them. */
export function commandsFor(
  shell: Shell,
  options: { readonly platformAdmin: boolean } = { platformAdmin: false },
): readonly Command[] {
  const commands: Command[] = GLOBAL_NAV.map((entry) =>
    navCommand(STUDIO, entry, "", [STUDIO]),
  );

  // Applications in the order the switcher offers them: pinned, then most
  // recently opened. What a person reaches for is what they reached for last.
  const entered = new Set(shell.entered.map((a) => a.manifest.id));
  const ordered: ShellApplication[] = [
    ...shell.entered,
    ...shell.applications.filter((a) => !entered.has(a.manifest.id)),
  ];

  for (const app of ordered) {
    if (enterable(app, shell.entitlementsKnown)) {
      commands.push(...applicationCommands(app));
    }
  }

  commands.push(
    ...ORGANIZATION_NAV.map((entry) => navCommand(ORGANIZATION, entry, "", [ORGANIZATION])),
  );
  commands.push({
    id: "nav:/marketplace",
    label: "Browse the marketplace",
    group: "Discover",
    icon: "🛍️",
    to: "/marketplace",
    kind: "navigate",
    keywords: ["catalog", "catalogue", "buy", "applications"],
  });

  for (const app of ordered) {
    if (enterable(app, shell.entitlementsKnown)) continue;
    const offer = offerCommand(app);
    if (offer) commands.push(offer);
  }

  if (options.platformAdmin) {
    commands.push({
      id: "nav:/studio/catalog-admin",
      label: "Catalog Admin",
      group: "Platform",
      icon: "🗄️",
      to: "/studio/catalog-admin",
      kind: "navigate",
      keywords: ["catalog", "admin", "platform"],
    });
  }

  return commands;
}

function score(command: Command, term: string): number {
  const label = command.label.toLowerCase();
  if (label.startsWith(term)) return 0;
  // A word start: "find" should reach "Findings" without reaching "notifications".
  if (label.split(/[^a-z0-9]+/).some((word) => word.startsWith(term))) return 1;
  if (label.includes(term)) return 2;
  if (command.keywords.some((keyword) => keyword.toLowerCase().includes(term))) return 3;
  return -1;
}

/**
 * The commands a term matches, best match first. Matching is literal and the
 * order is total, so the same term always produces the same list in the same
 * order: an interface that runs things must be predictable before it is clever.
 */
export function matchCommands(
  commands: readonly Command[],
  term: string,
  limit = 20,
): readonly Command[] {
  const needle = term.trim().slice(0, MAXIMUM_TERM).toLowerCase();
  const scored = commands
    .map((command, index) => ({ command, index, rank: needle ? score(command, needle) : 0 }))
    .filter((entry) => entry.rank >= 0);
  scored.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    // Declaration order, which is the order a person is offered them.
    return a.index - b.index;
  });
  return scored.slice(0, limit).map((entry) => entry.command);
}

/** Groups in first-appearance order, so headings follow the ranked list. */
export function groupCommands(
  commands: readonly Command[],
): readonly { readonly group: string; readonly commands: readonly Command[] }[] {
  const groups: { group: string; commands: Command[] }[] = [];
  for (const command of commands) {
    const existing = groups.find((g) => g.group === command.group);
    if (existing) existing.commands.push(command);
    else groups.push({ group: command.group, commands: [command] });
  }
  return groups;
}
